
import { enhancedFetch } from '../utils/api-helpers';
import type { Property } from '../types/property';
import { filterZestimateDescriptions } from '../utils';
import type { RentSearchParams } from './rent-search';
// Import centralized caching
import { propertyCache, cacheRentProperties } from '../utils/centralized-cache';
import { shouldExcludeFromFlow } from '../utils/status-gating';

/**
 * Round lot size values to maximum 2 decimal places
 * This prevents excessive decimal places like "0.382300000 acres"
 */
function roundLotSize(lotSize: number | null | undefined): number | null {
  if (lotSize === null || lotSize === undefined) {
    return null;
  }
  
  // Round to maximum 2 decimal places for all values
  return Math.round(lotSize * 100) / 100;
}

/**
 * Normalize address for better Zillow API compatibility
 * @param address - Raw address string
 * @returns Normalized address string
 */
function normalizeAddressForZillow(address: string): string {
  // Remove extra spaces and normalize commas
  let normalized = address.trim().replace(/\s+/g, ' ').replace(/,\s*/g, ', ');
  
  // Ensure proper comma separation between city, state, zip
  const parts = normalized.split(',');
  if (parts.length >= 3) {
    // Format: Street, City, State ZIP
    const street = parts[0].trim();
    const city = parts[1].trim();
    const stateZip = parts[2].trim();
    
    // Check if state and zip are separated
    const stateZipMatch = stateZip.match(/^([A-Z]{2})\s*(\d{5}(?:-\d{4})?)$/);
    if (stateZipMatch) {
      const [, state, zip] = stateZipMatch;
      normalized = `${street}, ${city}, ${state} ${zip}`;
    } else {
      normalized = `${street}, ${city}, ${stateZip}`;
    }
  }
  
  // Expand common abbreviations
  const abbreviations: Record<string, string> = {
    'Dr': 'Drive',
    'St': 'Street',
    'Ave': 'Avenue',
    'Blvd': 'Boulevard',
    'Ln': 'Lane',
    'Ct': 'Court',
    'Pl': 'Place',
    'Cir': 'Circle',
    'Rd': 'Road',
    'Way': 'Way'
  };
  
  // Replace abbreviations in the street part only
  const streetPart = normalized.split(',')[0];
  let updatedStreet = streetPart;
  for (const [abbr, full] of Object.entries(abbreviations)) {
    const regex = new RegExp(`\\b${abbr}\\b`, 'gi');
    updatedStreet = updatedStreet.replace(regex, full);
  }
  
  if (updatedStreet !== streetPart) {
    normalized = normalized.replace(streetPart, updatedStreet);
  }
  
  // Additional Zillow-specific normalizations
  // Remove any trailing punctuation
  normalized = normalized.replace(/[.,;]+$/, '');
  
  // Ensure there's a space between state and zip if missing
  normalized = normalized.replace(/([A-Z]{2})(\d{5})/g, '$1 $2');
  
  // Handle common address formatting issues
  normalized = normalized.replace(/\s*,\s*/g, ', '); // Normalize comma spacing
  normalized = normalized.replace(/\s+/g, ' '); // Normalize multiple spaces
  
  return normalized;
}

/**
 * Create a Property object from address search data
 * @param propertyData - Raw property data from Zillow API (can include details endpoint data)
 * @param address - Original address string (expected format: "Street, City, State ZIP" or just "Street")
 * @returns Property object
 */
function createPropertyFromAddressData(propertyData: any, address: string): Property {


  // Helper function to extract data from Zillow's quickFacts array
  const extractFromQuickFacts = (elementType: string): number => {
    if (!propertyData.quickFacts || !Array.isArray(propertyData.quickFacts)) {
      return 0;
    }
    
    const fact = propertyData.quickFacts.find((fact: any) => fact.elementType === elementType);
    if (fact && fact.value) {
      // Try different value structures
      let valueStr = '';
      
      if (fact.value.fullValue) {
        valueStr = fact.value.fullValue;
      } else if (fact.value.abbreviatedValue) {
        valueStr = fact.value.abbreviatedValue;
      } else if (typeof fact.value === 'string') {
        valueStr = fact.value;
      } else if (typeof fact.value === 'number') {
        return fact.value;
      }
      
      if (valueStr) {
        // Remove commas and extract number
        const cleanValue = valueStr.replace(/,/g, '').replace(/[^\d]/g, '');
        const value = parseInt(cleanValue);
        return isNaN(value) ? 0 : value;
      }
    }
    
    // Try alternative elementType names
    const alternativeTypes = {
      'beds': ['bedrooms', 'bedroom', 'bed'],
      'baths': ['bathrooms', 'bathroom', 'bath'],
      'livingArea': ['squareFeet', 'sqft', 'area', 'size']
    };
    
    if (alternativeTypes[elementType as keyof typeof alternativeTypes]) {
      for (const altType of alternativeTypes[elementType as keyof typeof alternativeTypes]) {
        const altFact = propertyData.quickFacts.find((fact: any) => fact.elementType === altType);
        if (altFact && altFact.value) {
          let valueStr = '';
          
          if (altFact.value.fullValue) {
            valueStr = altFact.value.fullValue;
          } else if (altFact.value.abbreviatedValue) {
            valueStr = altFact.value.abbreviatedValue;
          } else if (typeof altFact.value === 'string') {
            valueStr = altFact.value;
          } else if (typeof altFact.value === 'number') {
            return altFact.value;
          }
          
          if (valueStr) {
            // Remove commas and extract number
            const cleanValue = valueStr.replace(/,/g, '').replace(/[^\d]/g, '');
            const value = parseInt(cleanValue);
            if (!isNaN(value) && value > 0) return value;
          }
        }
      }
    }
    
    return 0;
  };
  
  // Helper function to extract data from formattedChip (alternative data source)
  const extractFromFormattedChip = (elementType: string): number => {
    if (!propertyData.formattedChip?.quickFacts || !Array.isArray(propertyData.formattedChip.quickFacts)) {
      return 0;
    }
    
    const fact = propertyData.formattedChip.quickFacts.find((fact: any) => fact.elementType === elementType);
    if (fact && fact.value && fact.value.fullValue) {
      // Clean numeric string like "2,345 sqft" → 2345
      const cleaned = String(fact.value.fullValue)
        .replace(/,/g, '')
        .replace(/[^\d]/g, '');
      const value = parseInt(cleaned, 10);
      return isNaN(value) ? 0 : value;
    }
    
    return 0;
  };

  // Enhanced data extraction with better field mapping
  const extractedData = {
    // Price extraction - try multiple field names
    price: propertyData.price || 
           propertyData.minPrice || 
           propertyData.maxPrice || 
           propertyData.listPrice || 
           propertyData.rentPrice ||
           propertyData.rentalPrice ||
           propertyData.monthlyRent ||
           propertyData.rent ||
           0,
    
    // Bedroom extraction - try quickFacts first, then fallback to direct fields
    bedrooms: (() => {
      const quickFactsBeds = extractFromQuickFacts('beds');
      if (quickFactsBeds > 0) return quickFactsBeds;
      
      const formattedChipBeds = extractFromFormattedChip('beds');
      if (formattedChipBeds > 0) return formattedChipBeds;
      
      const directBeds = propertyData.bedrooms || 
                         propertyData.beds || 
                         propertyData.bedroomCount || 
                         propertyData.bedroom ||
                         propertyData.bed ||
                         0;
      
      if (directBeds > 0) return directBeds;
      
      if (propertyData.resoFacts?.bedrooms) {
        const resoBeds = parseInt(propertyData.resoFacts.bedrooms);
        if (!isNaN(resoBeds) && resoBeds > 0) return resoBeds;
      }
      
      return 0;
    })(),
    
    // Bathroom extraction - try quickFacts first, then fallback to direct fields
    bathrooms: (() => {
      const quickFactsBaths = extractFromQuickFacts('baths');
      if (quickFactsBaths > 0) return quickFactsBaths;
      
      const formattedChipBaths = extractFromFormattedChip('baths');
      if (formattedChipBaths > 0) return formattedChipBaths;
      
      const directBaths = propertyData.bathrooms || 
                          propertyData.baths || 
                          propertyData.bathroomCount || 
                          propertyData.bathroom ||
                          propertyData.bath ||
                          0;
      
      if (directBaths > 0) return directBaths;
      
      if (propertyData.resoFacts?.bathrooms) {
        const resoBaths = parseInt(propertyData.resoFacts.bathrooms);
        if (!isNaN(resoBaths) && resoBaths > 0) return resoBaths;
      }
      
      return 0;
    })(),
    
    // Square footage extraction - try quickFacts first, then other fields
    squareFeet: (() => {
      const quickFactsSqft = extractFromQuickFacts('livingArea');
      if (quickFactsSqft > 0) return quickFactsSqft;
      
      const formattedChipSqft = extractFromFormattedChip('livingArea');
      if (formattedChipSqft > 0) return formattedChipSqft;
      
      const directSqft = propertyData.squareFeet || 
                         propertyData.livingArea || 
                         propertyData.homeSize || 
                         propertyData.size ||
                         propertyData.area ||
                         propertyData.sqft ||
                         propertyData.livingAreaValue ||
                         0;
      
      if (directSqft > 0) return directSqft;
      
      if (propertyData.resoFacts?.livingArea) {
        const resoSqft = parseInt(propertyData.resoFacts.livingArea);
        if (!isNaN(resoSqft) && resoSqft > 0) return resoSqft;
      }
      
      return 0;
    })(),
    
    // Year built extraction
    yearBuilt: (() => {
      const directYear = propertyData.yearBuilt || 
                         propertyData.year ||
                         propertyData.built ||
                         0;
      
      if (directYear > 0) return directYear;
      
      if (propertyData.resoFacts?.yearBuilt) {
        const resoYear = parseInt(propertyData.resoFacts.yearBuilt);
        if (!isNaN(resoYear) && resoYear > 0) return resoYear;
      }
      
      return 0;
    })(),
    
    // Lot size extraction - try multiple field names
    lotSize: (() => {
      const quickFactsLotSize = extractFromQuickFacts('lotSize');
      if (quickFactsLotSize > 0) return quickFactsLotSize;
      
      const formattedChipLotSize = extractFromFormattedChip('lotSize');
      if (formattedChipLotSize > 0) return formattedChipLotSize;
      
      const directLotSize = propertyData.lotSize || 
                           propertyData.lotSizeValue || 
                           propertyData.lot ||
                           propertyData.lotAreaValue ||
                           propertyData.lotArea ||
                           propertyData.lot_size ||
                           0;
      
      if (directLotSize > 0) return roundLotSize(directLotSize) || 0;
      
      if (propertyData.resoFacts?.lotSize) {
        const resoLotSize = parseInt(propertyData.resoFacts.lotSize);
        if (!isNaN(resoLotSize) && resoLotSize > 0) return roundLotSize(resoLotSize) || 0;
      }
      
      return 0;
    })(),
    
    // Title extraction with enhanced logic
    title: (() => {


      // Try direct title fields first
      if (propertyData.title) {
        return propertyData.title;
      }
      
      if (propertyData.name) {
        return propertyData.name;
      }
      
      if (propertyData.displayName) {
        return propertyData.displayName;
      }

      // NEW: Try to extract from formattedChip.location array (most reliable source)
      if (propertyData.formattedChip?.location && Array.isArray(propertyData.formattedChip.location)) {
        const locationArray = propertyData.formattedChip.location;
        
        // The first element (index 0) should be the property name/title
        if (locationArray.length > 0 && locationArray[0]) {
          const firstLocation = locationArray[0];
          const titleValue = firstLocation.fullValue || firstLocation;
          
          if (titleValue && typeof titleValue === 'string' && titleValue.trim()) {
            return titleValue.trim();
          }
        }
      }

      // Fallback to address if no title found
      return address;
    })(),
    
    // Address components - IMPROVED EXTRACTION LOGIC
    city: (() => {
      // Try multiple sources for city
      let cityValue = propertyData.address?.city || 
                     propertyData.city || 
                     null;
      
      // NEW: Try to extract from formattedChip.location array (most reliable source)
      if (!cityValue && propertyData.formattedChip?.location && Array.isArray(propertyData.formattedChip.location)) {
        const locationArray = propertyData.formattedChip.location;
        if (locationArray.length >= 3) {
          const lastLocation = locationArray[2]?.fullValue || locationArray[2];
          if (lastLocation && typeof lastLocation === 'string') {
            const cityMatch = lastLocation.match(/^([^,]+)/);
            if (cityMatch) {
              cityValue = cityMatch[1].trim();
            }
          }
        }
      }
      
      // Enhanced fallback extraction from search address
      if (!cityValue && address.includes(',')) {
        const cityMatch = address.match(/,\s*([^,]+?)(?:\s*,\s*[A-Z]{2}\s*\d{5}(?:-\d{4})?)?$/);
        if (cityMatch) {
          cityValue = cityMatch[1].trim();
        }
      }
      
      // If still no city, try to get it from the API response
      if (!cityValue) {
        cityValue = propertyData.city || 
                   propertyData.address?.city ||
                   propertyData.location?.city ||
                   propertyData.neighborhood?.city ||
                   null;
      }
      
      // Clean up city value
      if (cityValue) {
        cityValue = cityValue.replace(/\s+[A-Z]{2}\s*\d{5}(?:-\d{4})?$/, '').trim();
        return cityValue;
      }
      
      return '';
    })(),
    
    state: (() => {
      // Try multiple sources for state
      let stateValue = propertyData.address?.state || 
                      propertyData.state || 
                      null;
      
      // NEW: Try to extract from formattedChip.location array (most reliable source)
      if (!stateValue && propertyData.formattedChip?.location && Array.isArray(propertyData.formattedChip.location)) {
        const locationArray = propertyData.formattedChip.location;
        if (locationArray.length >= 3) {
          const lastLocation = locationArray[2]?.fullValue || locationArray[2];
          if (lastLocation && typeof lastLocation === 'string') {
            const stateMatch = lastLocation.match(/,\s*([A-Z]{2})\s*\d{5}(?:-\d{4})?$/);
            if (stateMatch) {
              stateValue = stateMatch[1].trim();
            }
          }
        }
      }
      
      // Enhanced fallback extraction from search address
      if (!stateValue && address.includes(',')) {
        const stateMatch = address.match(/,\s*[^,]+?,\s*([A-Z]{2})\s*\d{5}(?:-\d{4})?$/);
        if (stateMatch) {
          stateValue = stateMatch[1].trim();
        }
      }
      
      // If still no state, try to get it from the API response
      if (!stateValue) {
        stateValue = propertyData.state || 
                    propertyData.address?.state ||
                    propertyData.location?.state ||
                    null;
      }
      
      // Clean up state value
      if (stateValue) {
        stateValue = stateValue.replace(/\s*\d{5}(?:-\d{4})?$/, '').trim();
        if (stateValue.length === 2 && /^[A-Z]{2}$/i.test(stateValue)) {
          stateValue = stateValue.toUpperCase();
        }
        return stateValue;
      }
      
      return '';
    })(),
    
    zipCode: (() => {
      // Try multiple sources for zip code
      let zipValue = propertyData.address?.zipcode || 
                    propertyData.zipcode || 
                    propertyData.zip ||
                    null;
      
      // NEW: Try to extract from formattedChip.location array (most reliable source)
      if (!zipValue && propertyData.formattedChip?.location && Array.isArray(propertyData.formattedChip.location)) {
        const locationArray = propertyData.formattedChip.location;
        if (locationArray.length >= 3) {
          const lastLocation = locationArray[2]?.fullValue || locationArray[2];
          if (lastLocation && typeof lastLocation === 'string') {
            const zipMatch = lastLocation.match(/\d{5}(?:-\d{4})?$/);
            if (zipMatch) {
              zipValue = zipMatch[0];
            }
          }
        }
      }
      
      // Enhanced fallback extraction from search address
      if (!zipValue && address.includes(',')) {
        const zipMatch = address.match(/\d{5}(?:-\d{4})?$/);
        if (zipMatch) {
          zipValue = zipMatch[0];
        }
      }
      
      // If still no zip, try to get it from the API response
      if (!zipValue) {
        zipValue = propertyData.zipcode || 
                  propertyData.zip ||
                  propertyData.address?.zipcode ||
                  propertyData.location?.zipcode ||
                  null;
      }
      
      // Clean up zip value
      if (zipValue) {
        zipValue = zipValue.replace(/[^\d-]/g, '');
        return zipValue;
      }
      
      return '';
    })(),
    
    // Photos extraction - prioritize high-resolution photos
    photos: (() => {
      let photoUrls: string[] = [];
      
      // PRIORITY 1: Try photoUrlsHighRes first for high-resolution photos
      if (propertyData.photoUrlsHighRes && Array.isArray(propertyData.photoUrlsHighRes)) {
        photoUrls = propertyData.photoUrlsHighRes
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // FALLBACK: If no high-res photos, use regular photoUrls
      if (photoUrls.length === 0 && propertyData.photoUrls && Array.isArray(propertyData.photoUrls)) {
        photoUrls = propertyData.photoUrls
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // Try images if no photos found
      if (photoUrls.length === 0 && propertyData.images && Array.isArray(propertyData.images)) {
        photoUrls = propertyData.images
          .map((img: any) => img.url || img.imageUrl || img)
          .filter(Boolean);
      }
      
      // Try photos array if still no photos
      if (photoUrls.length === 0 && propertyData.photos && Array.isArray(propertyData.photos)) {
        photoUrls = propertyData.photos
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // Try media object if still no photos
      if (photoUrls.length === 0 && propertyData.media) {
        if (propertyData.media.allPropertyPhotos && Array.isArray(propertyData.media.allPropertyPhotos)) {
          photoUrls = propertyData.media.allPropertyPhotos;
        } else if (propertyData.media.propertyPhotoLinks && Array.isArray(propertyData.media.propertyPhotoLinks)) {
          photoUrls = propertyData.media.propertyPhotoLinks;
        }
      }
      

      
      return photoUrls;
    })(),
    
    // Description extraction
    description: (() => {
      const baseDescription = propertyData.description || 
                             propertyData.summary ||
                             propertyData.overview ||
                             '';
      
      // Add hardcoded text below each description
      if (baseDescription && baseDescription.trim()) {
        const filtered = filterZestimateDescriptions(baseDescription.trim());
        return filtered ? `${filtered}\n\nMultiple units at varying rates may be available.` : '';
      } else {
        return 'Multiple units at varying rates may be available.';
      }
    })(),
    
    // Location extraction
    lat: propertyData.latitude || 
         propertyData.location?.latitude || 
         propertyData.lat || 
         propertyData.coordinates?.lat ||
         0,
    
    lon: propertyData.longitude || 
         propertyData.location?.longitude || 
         propertyData.lng || 
         propertyData.coordinates?.lng ||
         0,
    
    // Property type extraction with proper mapping
    type: (() => {
      const propertyType = propertyData.homeType || 
                          propertyData.groupType || 
                          propertyData.propertyType || 
                          propertyData.type ||
                          'apartment';
      
      // Convert API value to UI label for filtering compatibility
      // Handle both uppercase and lowercase property types from different APIs
      const PROPERTY_TYPE_API_TO_UI_MAP: Record<string, string> = {
        'singleFamily': 'Single family home',
        'SINGLE_FAMILY': 'Single family home',
        'condo': 'Apartment',
        'CONDO': 'Apartment',
        'townhome': 'Townhome',
        'TOWNHOME': 'Townhome',
        'townhouse': 'Townhome',
        'TOWNHOUSE': 'Townhome',
        'Townhouse': 'Townhome',
        'multiFamily': 'Multi family home',
        'MULTI_FAMILY': 'Multi family home',
        'manufactured': 'Mobile',
        'MANUFACTURED': 'Mobile',
        'land': 'Land',
        'LAND': 'Land',
        'apartment': 'Apartment',
        'APARTMENT': 'Apartment',
        'apartmentComplex': 'Apartment', // Map apartmentComplex to Apartment for UI consistency
        'APARTMENT_COMPLEX': 'Apartment', // Map APARTMENT_COMPLEX to Apartment for UI consistency
      };
      
      const mappedType = PROPERTY_TYPE_API_TO_UI_MAP[propertyType] || propertyType;
      
      return mappedType;
    })()
  };

  const property: Property = {
    id: propertyData.zpid?.toString() || `address-${Date.now()}`,
    title: extractedData.title,
    address: address,
    city: extractedData.city,
    state: extractedData.state,
    zipCode: extractedData.zipCode,
    price: extractedData.price,
    details: {
      bedrooms: extractedData.bedrooms,
      bathrooms: extractedData.bathrooms,
      squareFeet: extractedData.squareFeet,
      yearBuilt: extractedData.yearBuilt,
      lotSize: { amount: roundLotSize(extractedData.lotSize) || 0 },
    },
    type: extractedData.type,
    status: 'forRent',
    photos: extractedData.photos,
    description: extractedData.description,
    location: {
      lat: extractedData.lat,
      lon: extractedData.lon,
    },
    url: propertyData.url || `https://www.zillow.com/rental/${propertyData.zpid}`,
    last_sync: new Date().toISOString(),
    source: 'zillow',
    source_id: String(propertyData.zpid || `address-${Date.now()}`),
    
  };


  
  // Add additional valuable fields from the API response
  (property as any).zestimate = propertyData.zestimate || null;
  (property as any).zestimateLowPercent = propertyData.zestimateLowPercent || null;
  (property as any).zestimateHighPercent = propertyData.zestimateHighPercent || null;
  (property as any).zestimateMinus30 = propertyData.zestimateMinus30 || null;
  (property as any).hoaFee = propertyData.hoaFee || null;
  (property as any).monthlyHoaFee = propertyData.monthlyHoaFee || null;
  (property as any).hoaFeeFrequency = propertyData.hoaFeeFrequency || null;
  (property as any).lotAreaUnits = propertyData.lotAreaUnits || null;
  (property as any).listingDataSource = propertyData.listingDataSource || null;
  (property as any).timeOnZillow = propertyData.timeOnZillow || null;
  (property as any).daysOnZillow = propertyData.daysOnZillow || null;
  (property as any).favoriteCount = propertyData.favoriteCount || 0;
  (property as any).pageViewCount = propertyData.pageViewCount || 0;
  (property as any).isZillowOwned = propertyData.isZillowOwned || false;
  (property as any).isIncomeRestricted = propertyData.isIncomeRestricted || false;
  (property as any).isHousingConnector = propertyData.isHousingConnector || false;
  (property as any).moveInReady = propertyData.moveInReady || false;
  (property as any).rentalApplicationsAcceptedType = propertyData.rentalApplicationsAcceptedType || null;
  (property as any).rentalDateAvailable = propertyData.rentalDateAvailable || null;
  (property as any).postingUrl = propertyData.postingUrl || null;
  (property as any).scheduleTourUrl = propertyData.scheduleTourUrl?.path || null;
  (property as any).contactAgentUrl = propertyData.contactAgentUrl?.path || null;
  (property as any).startOfferUrl = propertyData.startOfferUrl?.path || null;
  (property as any).streetViewImageUrl = propertyData.streetViewImageUrl || null;
  (property as any).timeZone = propertyData.timeZone || null;
  (property as any).whatILove = propertyData.whatILove || null;
  (property as any).showDescriptionDisclaimer = propertyData.showDescriptionDisclaimer || false;
  
  // Add rental metadata with enhanced data from details endpoint
  (property as any).rentalMetadata = {
    areApplicationsAccepted: propertyData.rental?.areApplicationsAccepted || false,
    isRoomForRent: propertyData.rental?.isRoomForRent || false,
    isBuildToRent: propertyData.rental?.isBuildToRent || false,
    isFeatured: propertyData.isFeatured || false,
    isShowcaseListing: propertyData.isShowcaseListing || false,
    matchingHomeCount: propertyData.matchingHomeCount || 0,
    providerListingID: propertyData.providerListingID || '',
    currency: propertyData.currency || 'usd',
    country: propertyData.country || 'usa',
    groupType: propertyData.groupType || 'apartment',
    listingDateTimeOnZillow: propertyData.listingDateTimeOnZillow || null,
    bestGuessTimeZone: propertyData.bestGuessTimeZone || null,
    marketingTreatments: propertyData.marketingTreatments || [],
    hasPriceRange: !!(propertyData.minPrice && propertyData.maxPrice && propertyData.minPrice !== propertyData.maxPrice) ||
                   !!(propertyData.priceMin && propertyData.priceMax && propertyData.priceMin !== propertyData.priceMax),
    minPrice: propertyData.minPrice || propertyData.priceMin || propertyData.price || 0,
    maxPrice: propertyData.maxPrice || propertyData.priceMax || propertyData.price || 0,
    unitsGroup: propertyData.unitsGroup || [],
    propertyTaxRate: propertyData.propertyTaxRate || null,
    hoaFee: propertyData.hoaFee || null,
    utilities: propertyData.utilities || [],
    parking: propertyData.parking || null,
    heating: propertyData.heating || null,
    cooling: propertyData.cooling || null,
    appliances: propertyData.appliances || [],
    flooring: propertyData.flooring || null,
    roof: propertyData.roof || null,
    exterior: propertyData.exterior || null,
    interior: propertyData.interior || null,
    lotFeatures: propertyData.lotFeatures || [],
    communityFeatures: propertyData.communityFeatures || [],
    
    // Enhanced rental-specific data from address API
    rentalApplicationsAcceptedType: propertyData.rentalApplicationsAcceptedType || null,
    moveInReady: propertyData.moveInReady || null,
    rentalDateAvailable: propertyData.rentalDateAvailable || null,
    listingDataSource: propertyData.listingDataSource || null,
    timeOnZillow: propertyData.timeOnZillow || null,
    daysOnZillow: propertyData.daysOnZillow || null,
    favoriteCount: propertyData.favoriteCount || 0,
    pageViewCount: propertyData.pageViewCount || 0,
    isZillowOwned: propertyData.isZillowOwned || false,
    isIncomeRestricted: propertyData.isIncomeRestricted || false,
    isHousingConnector: propertyData.isHousingConnector || false,
    postingUrl: propertyData.postingUrl || null,
    scheduleTourUrl: propertyData.scheduleTourUrl?.path || null,
    contactAgentUrl: propertyData.contactAgentUrl?.path || null,
    startOfferUrl: propertyData.startOfferUrl?.path || null,
    
    // Enhanced resoFacts data
    resoFacts: propertyData.resoFacts ? {
      homeType: propertyData.resoFacts.homeType,
      propertySubType: propertyData.resoFacts.propertySubType || [],
      yearBuilt: propertyData.resoFacts.yearBuilt,
      leaseTerm: propertyData.resoFacts.leaseTerm,
      furnished: propertyData.resoFacts.furnished,
      pricePerSquareFoot: propertyData.resoFacts.pricePerSquareFoot,
      parkingFeatures: propertyData.resoFacts.parkingFeatures || [],
      parkingCapacity: propertyData.resoFacts.parkingCapacity || 0,
      hasAttachedGarage: propertyData.resoFacts.hasAttachedGarage,
      hasCarport: propertyData.resoFacts.hasCarport,
      hasGarage: propertyData.resoFacts.hasGarage,
      hasOpenParking: propertyData.resoFacts.hasOpenParking,
      cooling: propertyData.resoFacts.cooling,
      hasCooling: propertyData.resoFacts.hasCooling,
      heating: propertyData.resoFacts.heating,
      depositsAndFees: propertyData.resoFacts.depositsAndFees,
      laundryFeatures: propertyData.resoFacts.laundryFeatures,
      flooring: propertyData.resoFacts.flooring,
      appliances: propertyData.resoFacts.appliances,
      hasPrivatePool: propertyData.resoFacts.hasPrivatePool,
      exteriorFeatures: propertyData.resoFacts.exteriorFeatures || [],
      accessibilityFeatures: propertyData.resoFacts.accessibilityFeatures,
      patioAndPorchFeatures: propertyData.resoFacts.patioAndPorchFeatures,
      communityFeatures: propertyData.resoFacts.communityFeatures,
      otherFacts: propertyData.resoFacts.otherFacts || [],
      atAGlanceFacts: propertyData.resoFacts.atAGlanceFacts || []
    } : null,
    
    // Contact information
    rentalListingOwnerContact: propertyData.rentalListingOwnerContact ? {
      displayName: propertyData.rentalListingOwnerContact.displayName,
      phoneNumber: propertyData.rentalListingOwnerContact.phoneNumber,
      photoUrl: propertyData.rentalListingOwnerContact.photoUrl
    } : null,
    
    // Owner reputation data
    rentalListingOwnerReputation: propertyData.rentalListingOwnerReputation ? {
      contactCount: propertyData.rentalListingOwnerReputation.contactCount,
      applicationCount: propertyData.rentalListingOwnerReputation.applicationCount
    } : null,
    
    // Listing metadata
    listingMetadata: propertyData.listingMetadata ? {
      isAdsRestricted: propertyData.listingMetadata.isAdsRestricted
    } : null,
    
    // Room for rent details
    roomForRent: propertyData.roomForRent,
    listingSubType: propertyData.listing_sub_type ? {
      isRoomForRent: propertyData.listing_sub_type.is_roomForRent
    } : null,
    
    // Additional property details
    whatILove: propertyData.whatILove,
    showDescriptionDisclaimer: propertyData.showDescriptionDisclaimer,
    streetViewImageUrl: propertyData.streetViewImageUrl,
    timeZone: propertyData.timeZone,
    virtualTourUrl: propertyData.virtualTourUrl,
    primaryPublicVideo: propertyData.primaryPublicVideo,
    unassistedShowing: propertyData.unassistedShowing,
    homeInsights: propertyData.homeInsights,
    richMediaVideos: propertyData.richMediaVideos,
    richMedia: propertyData.richMedia,
    homeValueChartData: propertyData.homeValueChartData || [],
    
    // Photo-related data
    imageLink: propertyData.imageLink,
    photoUrlsHighRes: propertyData.photoUrlsHighRes || [],
    photoMetadata: []
  };
  
  // Add price range at top level if available
  if ((property as any).rentalMetadata.hasPriceRange) {
    (property as any).priceRange = {
      min: (property as any).rentalMetadata.minPrice,
      max: (property as any).rentalMetadata.maxPrice,
      display: `$${(property as any).rentalMetadata.minPrice.toLocaleString()} - $${(property as any).rentalMetadata.maxPrice.toLocaleString()}`
    };
  }
  
  // Add additional property details if available from details endpoint
  if (propertyData.homeDetails) {
    (property as any).homeDetails = propertyData.homeDetails;
  }
  
  if (propertyData.neighborhood) {
    (property as any).neighborhood = propertyData.neighborhood;
  }
  
  if (propertyData.schools) {
    (property as any).schools = propertyData.schools;
  }
  
  return property;
}

/**
 * Try to search for a property using a specific address format
 */
async function tryAddressSearch(addressFormat: string, originalAddress: string): Promise<Property[] | null> {
  // Minimal delay between attempts to avoid rate limiting but keep it fast
  await new Promise(resolve => setTimeout(resolve, 50)); // Reduced from 100ms to 50ms
  
  const searchUrl = '/api/properties/search-address';
  const urlParams = new URLSearchParams();
  urlParams.append('address', addressFormat);
  
  try {

    
    const response = await enhancedFetch(`${searchUrl}?${urlParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // Check if it's a Zillow validation error
      if (response.status === 400 && errorText.includes('not a detailed address')) {
        return null; // Try next format
      }
      
      // Check for rate limiting
      if (response.status === 429) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return null; // Try next format
      }
      
      throw new Error(`Address search failed: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    try {
      const sample = (data as any)?.data ? (data as any).data : data;
    } catch {}

    if (!data.data) {
      return null; // Try next format
    }
    
    // Transform the address search result to a Property object
    const propertyData = data.data;
    
    // Guard: exclude non-rental results (e.g., FOR_SALE) from rent address flow
    if (shouldExcludeFromFlow(propertyData, 'rent')) {
      return null; // Let other flows (buy/sold) handle it
    }
    
    
    
    // The address endpoint already has comprehensive data - no need to call details endpoint
    // Extract data directly from the address endpoint response
    const detailedPropertyData = propertyData;
    
    const property: Property = createPropertyFromAddressData(detailedPropertyData, originalAddress);
    
    
    
    // Don't cache here - let the higher-level function handle caching
    // This prevents duplicate caching calls that cause race conditions
    
    return [property];
    
  } catch (error) {
    throw error;
  }
}

/**
 * Search rental properties by specific address
 * This function detects if the input is an address and searches accordingly
 */
export const searchRentPropertiesByAddress = async (
  address: string, 
  searchParams: Partial<RentSearchParams> = {}, 
  retryCount: number = 0,
  fallbackSearch?: (input: string, params: Partial<RentSearchParams>) => Promise<Property[]>
): Promise<Property[]> => {
  try {
    // Check if we already have this exact address in cache
    const normalizedAddress = normalizeAddressForZillow(address);
    const cacheKey = `rent-address-search-${normalizedAddress.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;
    
    const cachedResult = propertyCache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }
    
    // Prevent infinite loops
    if (retryCount > 2) {
      if (fallbackSearch) {
        return await fallbackSearch(address, searchParams);
      }
      throw new Error('Max retries reached and no fallback search provided');
    }
    
    // Normalize the address for better Zillow API compatibility
    const normalizedAddressForSearch = normalizeAddressForZillow(address);
    
    // Detect if this is an address-based search (contains street number and address)
    // Pattern: starts with number + space, and contains street-like patterns
    const isAddressSearch = /^\d+\s+/.test(normalizedAddressForSearch.trim()) && 
                           (normalizedAddressForSearch.includes(',') || 
                            /[A-Z]{2}/.test(normalizedAddressForSearch) || 
                            /\b(?:Rd|Road|St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(normalizedAddressForSearch));
    
    if (!isAddressSearch) {
      if (fallbackSearch) {
        return await fallbackSearch(address, searchParams);
      }
      throw new Error('Input does not appear to be an address and no fallback search provided');
    }
    
    // Try different address formats to improve success rate, but be smarter about it
    const addressFormats = [
      normalizedAddressForSearch, // Try normalized version first (most likely to work)
      address, // Try original address
      // Only try these fallbacks if the first two fail
      address.replace(/,\s*\d{5}(?:-\d{4})?$/, ''), // Without zip
      address.split(',').slice(0, 2).join(',') // Just street and city
    ];
    
    // Track processed addresses to avoid duplicates
    const processedAddresses = new Set<string>();
    
    // Reduce delay between attempts for faster response
    for (let i = 0; i < addressFormats.length; i++) {
      const currentFormat = addressFormats[i];
      
      // Skip if we've already processed this exact format
      if (processedAddresses.has(currentFormat)) {
        continue;
      }
      
      processedAddresses.add(currentFormat);
      
      try {
        const result = await tryAddressSearch(currentFormat, address);
        if (result) {
          // Cache the successful result using centralized cache function
          // This handles both local caching and database upsert
          try {
            cacheRentProperties(result, address, undefined);
          } catch (error) {
            // Failed to cache address-based rent search results
          }
          
          // Return results immediately - don't wait for database caching
          return result;
        }
      } catch (error) {
        if (i === addressFormats.length - 1) {
          throw error;
        }
      }
    }
    
    // If all formats fail, fall back to location search
    if (fallbackSearch) {
      return await fallbackSearch(address, searchParams);
    }
    throw new Error('All address formats failed and no fallback search provided');
    
  } catch (error) {
    // Fall back to regular location search if address search fails
    if (fallbackSearch) {
      return await fallbackSearch(address, searchParams);
    }
    throw error;
  }
};

// Cleanup is now handled by the centralized cache system

/**
 * Get address suggestions for rental properties
 * This provides autocomplete functionality for address searches
 */
export const getRentalAddressSuggestions = async (userInput: string): Promise<any[]> => {
  try {
    const autoCompleteUrl = '/api/properties/auto-complete';
    const params = new URLSearchParams();
    params.append('query', userInput);

    const response = await enhancedFetch(
      `${autoCompleteUrl}?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();
    
    // Filter for address-type suggestions
    if (data.data && Array.isArray(data.data)) {
      const addressSuggestions = data.data
        .filter((item: any) => item.resultType === "Address" && item.display)
        .map((item: any) => ({
          display: item.display,
          resultType: item.resultType,
          metaData: item.metaData
        }));
      
      return addressSuggestions;
    }

    return [];
  } catch (error) {
    return [];
  }
};